import { useState } from 'react';
import { pedir } from '../lib/api';
import { tx } from '../i18n';
import { vivo } from '../i18n/vivo';
import { LICENCIA, permisos, tonoLicencia } from '../lib/appliance';
import { useConsulta } from '../lib/consulta';
import { cuando, miles, soloDia } from '../lib/formato';
import { useSesion } from '../sesion/sesion';
import { Consulta, Pastilla, primero, Tabla, Vacio, Ventana } from '../ui/piezas';

interface LicenciaFila {
  id: string;
  plan: 'basico' | 'pro' | 'ilimitado';
  equipos: number | null;
  emitida: string;
  vence: string;
  quien: string;
  creada: string;
  entregada: string | null;
  revocada: string | null;
  motivo: string | null;
}

interface Licencias {
  vigente: (LicenciaFila & { estado: string; dias: number }) | null;
  historial: LicenciaFila[];
}

/** `vivo`: los nombres de plan siguen al idioma aunque esto sea una constante de módulo. */
const PLAN: Record<string, string> = vivo(() => tx.licencia.planes);

/** Cuántos días faltan o hace cuántos venció, dicho como lo diría una persona. */
function plazo(dias: number): string {
  if (dias === 0) return tx.licencia.venceHoy;
  return dias > 0 ? tx.licencia.faltan(dias) : tx.licencia.vencioHace(-dias);
}

/**
 * La licencia de un appliance (docs/centro.md §4): qué tiene vendido, hasta cuándo, y la
 * renovación. La firma el Centro y el appliance la valida solo, así que acá no hay nada que
 * "activar": se emite y se entrega.
 */
export function Licencia({ applianceId, enLinea, alCambiar }: { applianceId: string; enLinea: boolean; alCambiar: (aviso: string) => void }) {
  const { usuario } = useSesion();
  const puede = permisos(usuario?.rol);
  const q = useConsulta<Licencias>(`/appliances/${applianceId}/licencias`);
  const [emitir, setEmitir] = useState(false);
  const [revocar, setRevocar] = useState<LicenciaFila | null>(null);
  const [plan, setPlan] = useState<'basico' | 'pro' | 'ilimitado'>('pro');
  const [equipos, setEquipos] = useState('100');
  const [vence, setVence] = useState(unAñoDesdeHoy());
  const [motivo, setMotivo] = useState('');

  const v = q.data?.vigente ?? null;
  const historial = (q.data?.historial ?? []).filter((l) => l.id !== v?.id);

  return (
    <section className="panel">
      <h2 className="rotulo">
        {tx.licencia.titulo}
        {v && (
          <span className="aux">
            <Pastilla tono={tonoLicencia(v.estado)}>{LICENCIA[v.estado] ?? v.estado}</Pastilla>
          </span>
        )}
      </h2>

      <Consulta q={q}>
        {!v ? (
          <Vacio>{tx.licencia.sinLicencia}</Vacio>
        ) : (
          <dl className="ficha">
            <div className="dato">
              <dt>{tx.licencia.plan}</dt>
              <dd>
                {PLAN[v.plan] ?? v.plan} · {v.equipos === null ? tx.licencia.sinTope : tx.licencia.equipos(miles(v.equipos))}
              </dd>
            </div>
            <div className="dato">
              <dt>{tx.licencia.vence}</dt>
              <dd>
                {soloDia(v.vence)} · {plazo(v.dias)}
              </dd>
            </div>
            <div className="dato">
              <dt>{tx.licencia.emitida}</dt>
              <dd>
                {soloDia(v.emitida)} · {v.quien}
              </dd>
            </div>
            <div className="dato">
              <dt>{tx.licencia.entregada}</dt>
              <dd>{v.entregada ? cuando(v.entregada) : enLinea ? tx.licencia.todaviaNo : tx.licencia.esperandoAlAppliance}</dd>
            </div>
          </dl>
        )}

        <div className="botones">
          {puede.emitirLicencia && (
            <button type="button" className="btn prim" onClick={() => setEmitir(true)}>
              {v ? tx.licencia.renovar : tx.licencia.emitir}
            </button>
          )}
          {v && (
            <>
              <button
                type="button"
                className="btn"
                onClick={() =>
                  void pedir<{ entregada: boolean }>(`/appliances/${applianceId}/licencias/reenviar`, { metodo: 'POST' }).then((r) =>
                    alCambiar(r.entregada ? tx.licencia.mandada : tx.licencia.mandadaDespues),
                  )
                }
              >
                {tx.licencia.reenviar}
              </button>
              {/* Para una red aislada: se baja el archivo y se pega en el appliance. */}
              <a className="btn" href={`/api/appliances/${applianceId}/licencias/archivo`}>
                {tx.licencia.bajarArchivo}
              </a>
              {puede.revocarLicencia && (
                <button type="button" className="btn peligro" onClick={() => setRevocar(v)}>
                  {tx.licencia.revocar}
                </button>
              )}
            </>
          )}
        </div>

        {historial.length > 0 && (
          <>
            <h3 className="sub-rotulo">{tx.licencia.anteriores}</h3>
            <Tabla>
              <thead>
                <tr>
                  <th>{tx.licencia.plan}</th>
                  <th>{tx.comun.equipos}</th>
                  <th>{tx.licencia.hasta}</th>
                  <th>{tx.licencia.emitidaPor}</th>
                  <th>{tx.comun.estado}</th>
                </tr>
              </thead>
              <tbody>
                {historial.map((l) => (
                  <tr key={l.id}>
                    <td>{PLAN[l.plan] ?? l.plan}</td>
                    <td>{l.equipos === null ? '—' : miles(l.equipos)}</td>
                    <td>{soloDia(l.vence)}</td>
                    <td>{l.quien}</td>
                    <td>{l.revocada ? tx.licencia.revocadaPor(l.motivo ?? '') : tx.licencia.reemplazada}</td>
                  </tr>
                ))}
              </tbody>
            </Tabla>
          </>
        )}
      </Consulta>

      {emitir && (
        <Ventana
          titulo={v ? tx.licencia.renovarTitulo : tx.licencia.emitir}
          boton={v ? tx.licencia.renovar : 'Issue'}
          alCerrar={() => setEmitir(false)}
          alEnviar={async () => {
            const falta = primero(
              plan !== 'ilimitado' && Number(equipos) < 1 && tx.licencia.faltaEquipos,
              !vence && tx.licencia.faltaVence,
            );
            if (falta) return falta;
            await pedir(`/appliances/${applianceId}/licencias`, {
              metodo: 'POST',
              cuerpo: {
                plan,
                vence,
                ...(plan === 'ilimitado' ? {} : { equipos: Number(equipos) }),
              },
            });
            await q.refetch();
            setEmitir(false);
            alCambiar(tx.licencia.emitidaOk);
          }}
        >
          <label className="campo" htmlFor="lic-plan">
            {tx.licencia.plan}
            <select id="lic-plan" className="lista-sel" value={plan} onChange={(e) => setPlan(e.target.value as typeof plan)}>
              <option value="basico">{tx.licencia.planes.basico}</option>
              <option value="pro">{tx.licencia.planes.pro}</option>
              <option value="ilimitado">{tx.licencia.ilimitado}</option>
            </select>
          </label>
          {plan !== 'ilimitado' && (
            <label className="campo" htmlFor="lic-equipos">
              {tx.licencia.equiposQueCubre}
              <input id="lic-equipos" className="entrada" type="number" min={1} value={equipos} onChange={(e) => setEquipos(e.target.value)} />
            </label>
          )}
          <label className="campo" htmlFor="lic-vence">
            {tx.licencia.venceEl}
            <input id="lic-vence" className="entrada" type="date" value={vence} onChange={(e) => setVence(e.target.value)} />
          </label>
          <p className="nota">
            {tx.licencia.queImplica}
          </p>
        </Ventana>
      )}

      {revocar && (
        <Ventana
          titulo={tx.licencia.revocarTitulo}
          boton={tx.licencia.revocar}
          peligro
          alCerrar={() => setRevocar(null)}
          alEnviar={async () => {
            if (!motivo.trim()) return tx.licencia.faltaMotivo;
            await pedir(`/licencias/${revocar.id}/revocar`, {
              metodo: 'POST',
              cuerpo: { motivo: motivo.trim() },
            });
            await q.refetch();
            setRevocar(null);
            setMotivo('');
            alCambiar(tx.licencia.revocada);
          }}
        >
          <p>{tx.licencia.revocarQue}</p>
          <label className="campo" htmlFor="lic-motivo">
            {tx.licencia.porQue}
            <input
              id="lic-motivo"
              className="entrada"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder={tx.licencia.porQueEjemplo}
            />
          </label>
        </Ventana>
      )}
    </section>
  );
}

function unAñoDesdeHoy(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}
